import { Flex, Heading, Link, Text } from '@skin-seoul/design-system';
import type { ReactNode, ElementType } from 'react';
import { Convert } from '@skin-seoul/utils';
import { LucideIcon } from '../../components/LucideIcon';

type Props = {
  href?: string | (() => void);
  theme?: 'dark' | 'light';
  title: ReactNode;
  subtitle?: ReactNode;
  /**
   * Custom Link component to use for navigation.
   * Useful for injecting Next.js Link in webapp or other routing solutions.
   * @default Link from @skin-seoul/design-system
   */
  linkComponent?: ElementType;
} & React.ComponentProps<typeof Flex>;

const SectionHeader = ({ theme = 'light', title, subtitle, href, linkComponent, ...props }: Props) => {
  const isDark = theme === 'dark';
  const LinkComponent = linkComponent || Link;
  return (
    <Flex direction="column" width="100%" gap={2} {...props}>
      <Flex alignItems="center" gap={4} width={{ base: '100%', tablet: 'fit-content' }}>
        <Convert
          value={href}
          formatter={(href: string | (() => void)) => {
            const isFunction = typeof href === 'function';

            if (isFunction) {
              return (
                <Flex
                  as="button"
                  onClick={href}
                  gap={6}
                  width="100%"
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  justifyContent={{ base: 'space-between', mobile: 'start' }}
                  color={isDark ? 'generic.0' : 'generic.100'}
                  cursor="pointer"
                  background="transparent"
                  border="none"
                  _hover={{
                    '& h2': {
                      color: 'primary.400',
                    },
                    '& svg': {
                      color: 'primary.400',
                    },
                  }}
                  _active={{
                    '& h2': {
                      color: isDark ? 'primary.200' : 'primary.600',
                    },
                    '& svg': {
                      color: isDark ? 'primary.200' : 'primary.600',
                    },
                  }}
                >
                  <>
                    <Heading as="h2" textStyle="heading.h5" fontWeight="normal">
                      {title}
                    </Heading>
                    <LucideIcon name="ChevronRight" boxSize={7} />
                  </>
                </Flex>
              );
            }

            return (
              <LinkComponent
                unstyled
                href={href as string}
                gap={6}
                width="100%"
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent={{ base: 'space-between', mobile: 'start' }}
                color={isDark ? 'generic.0' : 'generic.100'}
                _hover={{
                  '& h2': {
                    color: 'primary.400',
                  },
                  '& svg': {
                    color: 'primary.400',
                  },
                }}
                _active={{
                  '& h2': {
                    color: isDark ? 'primary.200' : 'primary.600',
                  },
                  '& svg': {
                    color: isDark ? 'primary.200' : 'primary.600',
                  },
                }}
              >
                <>
                  <Heading as="h2" textStyle="heading.h5" fontWeight="normal">
                    {title}
                  </Heading>
                  <LucideIcon name="ChevronRight" boxSize={7} />
                </>
              </LinkComponent>
            );
          }}
          defaultValue={
            <Heading as="h2" color={isDark ? 'generic.0' : 'generic.100'} textStyle="heading.h5" fontWeight="normal">
              {title}
            </Heading>
          }
        />
      </Flex>

      <Text color={isDark ? 'neutral.100' : 'neutral.600'} textStyle="paragraph.medium" fontWeight="regular">
        {subtitle}
      </Text>
    </Flex>
  );
};

export default SectionHeader;
